import {useMutation, useQuery} from "@apollo/client";
import classNames from "classnames";
import {useContext, useEffect, useRef, useState} from "react";
import {AccordionEventKey} from "react-bootstrap/esm/AccordionContext";
import {toast} from "react-hot-toast";
import {HiOutlineLockClosed} from "react-icons/hi";

import {Accordion} from "@/components/accordion/Accordion";
import Button from "@/components/button/Button";
import {useCurrentUserContext} from "@/components/context/hooks";
import {NewCheckVaultItemsContext} from "@/components/context/NewCheckVaultItemsContextInterface.tsx";
import {Icon} from "@/components/icon/Icon";
import {LoadingContainer} from "@/components/placeholder/LoadingContainer";
import {captureMessageForReporting} from "@/lib/errorReporting.tsx";
import {UpErrorBoundary} from "@/lib/handlers/UpErrorBoundary";
import {
  GQL_SERVICE_VARIABLES_CREATE_MULTIPLE,
  GQL_SERVICE_VARIABLES_GROUP_BY_CREDENTIAL_ID_QUERY,
} from "@/lib/queries/serviceVariables.ts";
import {GenericMutationResponse} from "@/lib/types/common.ts";
import {Credentials} from "@/lib/types/credentials.ts";
import {
  ServiceVariableGroupByCredential,
  ServiceVariableGroupByCredentialResponse,
  ServiceVariableInput,
} from "@/lib/types/serviceVariables.ts";
import {
  CredentialOptionType,
  CredentialSelect,
} from "@/pages/checks/checkVaultServiceVariables/CredentialSelect/CredentialSelect.tsx";
import {
  CreateCredentialServiceVariableInputsForCheck,
  GetVariableNamesFromServiceVariableGroupByCredential,
} from "@/pages/checks/checkVaultServiceVariables/serviceVariableUtils.ts";
import {VaultItem} from "@/pages/checks/checkVaultServiceVariables/VaultItem.tsx";
import {CredentialCreateModal} from "@/pages/vault/vaultForm/CredentialCreateModal";

import styles from "./CheckVaultServiceField.module.scss";

interface CheckVaultServiceFieldProps {
  className?: string;
}

export const CheckVaultServiceField = ({className}: CheckVaultServiceFieldProps) => {
  const {checkId: contextCheckId, vaultItems, addVaultItem} = useContext(NewCheckVaultItemsContext);
  const {user} = useCurrentUserContext();
  const [showCreateCredentialModal, setShowCreateCredentialModal] = useState(false);
  const vaultItemsRef = useRef<HTMLDivElement>(null);
  const [vaultItemsScrollbar, setVaultItemsScrollbar] = useState(false);
  const [accordionActiveKey, setAccordionActiveKey] = useState<AccordionEventKey>(undefined);
  const accordionItemKey = "0";

  const [createServiceVariablesMultiple] = useMutation<GenericMutationResponse>(GQL_SERVICE_VARIABLES_CREATE_MULTIPLE, {
    refetchQueries: [
      {
        query: GQL_SERVICE_VARIABLES_GROUP_BY_CREDENTIAL_ID_QUERY,
        variables: {
          service_id: contextCheckId,
        },
      },
    ],
    context: {
      noToast: true,
    },
  });

  const {loading, data, previousData} = useQuery<ServiceVariableGroupByCredentialResponse>(
    GQL_SERVICE_VARIABLES_GROUP_BY_CREDENTIAL_ID_QUERY,
    {
      variables: {
        service_id: contextCheckId,
      },
      skip: !contextCheckId,
    }
  );

  const credentialSelectHandler = async (value: CredentialOptionType | null) => {
    // for the existing checks we could have variable names be generated on the backend on initial creation if none are provided.
    if (value) {
      const partialServiceVariables = CreateCredentialServiceVariableInputsForCheck(
        value.value,
        GetVariableNamesFromServiceVariableGroupByCredential(
          contextCheckId ? vaultItems : data?.response.data ? data.response.data : []
        )
      );

      if (!contextCheckId) {
        const serviceVariables = partialServiceVariables.map((serviceVariable) => {
          return {
            account: "",
            service: "",
            credential: value.value,
            variable_name: serviceVariable.variable_name,
            property_name: serviceVariable.property_name,
          };
        });
        const serviceVariableGroupByCredential = {
          credential: value.value,
          service_variables: serviceVariables,
        };
        addVaultItem(serviceVariableGroupByCredential);
        return;
      }
      if (!contextCheckId) {
        captureMessageForReporting("Check ID is not defined, cannot add Vault Items to Check.");
        toast.error("Something went wrong adding a Vault Item to the Check.");
        return;
      }
      const serviceVariables: ServiceVariableInput[] = partialServiceVariables.map((serviceVariable) => {
        return {
          ...serviceVariable,
          service_id: contextCheckId!,
        };
      });
      const response = await createServiceVariablesMultiple({variables: {input: {serviceVariables: serviceVariables}}});
      if (response.data?.response?.status === "ERROR") {
        if (response.data?.response?.message?.__all__.includes("That credential already exists on this Check")) {
          toast.error("That credential already exists on this Check");
        } else {
          toast.error("Something went wrong adding a Vault Item to the Check.");
        }
      }
    }
  };

  const handleCredentialCreate = (credential: Credentials) => {
    toast.success("Vault Item created successfully.");
    credentialSelectHandler({value: credential, label: credential.display_name});
  };

  const newVaultItems: ServiceVariableGroupByCredential[] = !contextCheckId ? vaultItems : data?.response.data || [];

  const checkVaultItemsScrollbar = () => {
    const container = vaultItemsRef.current;
    setVaultItemsScrollbar(!!container && container.scrollHeight > container.clientHeight);
  };

  useEffect(() => {
    checkVaultItemsScrollbar();
    window.addEventListener("resize", checkVaultItemsScrollbar);
    return () => window.removeEventListener("resize", checkVaultItemsScrollbar);
  }, [newVaultItems.length]);

  if (loading && !previousData && contextCheckId) {
    return <LoadingContainer text="Loading..." className="d-flex justify-content-center my-5" />;
  }

  return (
    <Accordion
      className={className}
      activeKey={accordionActiveKey}
      onSelect={(eventKey) => setAccordionActiveKey(eventKey)}
    >
      <Accordion.Item eventKey={accordionItemKey}>
        <Accordion.Header
          className={`d-flex justify-content-between align-items-center p-4 pulse-bg-gray-50 border pulse-border-gray-400 ${accordionActiveKey === accordionItemKey ? "rounded-top-3" : "rounded-3"}`}
        >
          <div className="d-flex flex-column gap-2">
            <div className={"d-flex"}>
              <div className={"pulseTextH3Bold d-flex align-items-center gap-1"}>
                <Icon size="kpi" removeDivDimensions icon={HiOutlineLockClosed} /> Vault Items
              </div>
            </div>
            <div className={"pulseTextBodySmall pulse-color-gray-600"}>
              Add Vault items to securely store credentials like passwords and certificates for use in your Checks.
            </div>
          </div>
        </Accordion.Header>
        <Accordion.Body className="d-flex flex-column h-100 gap-4 p-4 rounded-bottom-3 position-relative border border-top-0 pulse-border-gray-400">
          <div id={"Vault Item Select"}>
            <CredentialSelect onChange={credentialSelectHandler} />
          </div>
          <UpErrorBoundary>
            {newVaultItems.length > 0 && (
              <div className={"d-flex flex-column gap-3"}>
                <div
                  ref={vaultItemsRef}
                  className={classNames(styles.vaultItems, {"pe-2": vaultItemsScrollbar}, "d-flex flex-column gap-4")}
                >
                  {newVaultItems.map((vaultItem) => {
                    return (
                      <VaultItem
                        credential={vaultItem.credential}
                        serviceVariables={vaultItem.service_variables}
                        key={vaultItem.credential.id}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </UpErrorBoundary>
          <div className="d-flex justify-content-center">
            <Button variant="secondary" onClick={() => setShowCreateCredentialModal(true)} className="w-100">
              Create Vault Item
            </Button>
          </div>
          <UpErrorBoundary>
            {user?.has_write_access && (
              <CredentialCreateModal
                show={showCreateCredentialModal}
                setShow={setShowCreateCredentialModal}
                handleCreate={handleCredentialCreate}
              />
            )}
          </UpErrorBoundary>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
};
